import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, User as UserIcon, Loader2, Newspaper, Tag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import PromotionSlot from '../components/PromotionSlot';
import { applyPageMetadata, metadataForPath, SITE_URL } from '../seo/metadata';
import {
  NewsDetailPublic, NewsListItem,
  getPublicNews, getLatestNews, newsImageUrl,
} from '../api/news';

export default function NewsArticlePage() {
  const { slug = '' } = useParams();
  const { t, language } = useLanguage();

  const [post, setPost] = useState<NewsDetailPublic | null>(null);
  const [latest, setLatest] = useState<NewsListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(language === 'kk' ? 'kk-KZ' : 'ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    setPost(null);
    getPublicNews(slug, language)
      .then((data) => {
        if (!active) return;
        setPost(data);
      })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });

    getLatestNews(4).then((l) => { if (active) setLatest(l.filter((i) => i.slug !== slug)); }).catch(() => {});

    return () => {
      active = false;
    };
  }, [slug, language]);

  useEffect(() => {
    const path = `/news/${encodeURIComponent(slug)}`;
    if (post && post.slug === slug && !loading && !notFound) {
      const title = `${post.seo_title || post.title} — G-Launcher`;
      const image = newsImageUrl(post.cover_url);
      applyPageMetadata({ path, title, description: post.seo_description || post.excerpt || post.title,
        indexable: true, article: true, image,
        schema: [{ '@type': 'Article', headline: post.title, description: post.excerpt,
          ...(post.published_at ? { datePublished: post.published_at } : {}),
          ...(image ? { image: [image] } : {}),
          mainEntityOfPage: `${SITE_URL}${path}`, inLanguage: post.lang,
          publisher: { '@type': 'Organization', name: 'G-Launcher', url: SITE_URL },
        }],
      });
    } else {
      applyPageMetadata({ ...metadataForPath(path), title: notFound ? 'Публикация не найдена — G-Launcher' : 'Публикация — G-Launcher' });
    }
  }, [post, loading, notFound, slug, language]);

  const listTitle = (i: { title_ru: string; title_kk: string }) =>
    language === 'kk' ? i.title_kk || i.title_ru : i.title_ru || i.title_kk;

  if (loading) {
    return <div className="min-h-screen flex justify-center items-center text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-4">
        <Newspaper className="w-14 h-14 text-gray-200 mb-4" />
        <h1 className="text-xl font-extrabold text-gray-900 mb-2">{t('news.notFoundTitle')}</h1>
        <p className="text-gray-500 mb-6">{t('news.notFoundText')}</p>
        <Link to="/news" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-goszakup-600 text-white text-sm font-semibold hover:bg-goszakup-700">
          <ArrowLeft className="w-4 h-4" /> {t('news.backToList')}
        </Link>
      </div>
    );
  }

  const cover = newsImageUrl(post.cover_url);

  return (
    <div className="min-h-screen bg-slate-50">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/news" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-goszakup-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> {t('news.backToList')}
        </Link>

        {/* Header */}
        <header className="mb-6">
          {post.category_title && (
            <span className="inline-block text-[11px] font-bold text-goszakup-700 bg-goszakup-50 px-2.5 py-1 rounded-full mb-3">{post.category_title}</span>
          )}
          <h1 className="text-2xl sm:text-4xl font-extrabold text-gray-900 tracking-tight leading-tight mb-3">{post.title}</h1>
          {post.excerpt && <p className="text-lg text-gray-500">{post.excerpt}</p>}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 mt-4">
            {post.published_at && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(post.published_at)}</span>}
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.reading_time} {t('news.min')}</span>
            {post.author_name && <span className="flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" /> {post.author_name}</span>}
          </div>
        </header>

        {/* Cover */}
        {cover && (
          <div className="rounded-3xl overflow-hidden mb-8 bg-gray-100 aspect-video">
            <img src={cover} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Body */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-10">
          {post.body_html.trim() ? (
            <div className="html-document-container" dangerouslySetInnerHTML={{ __html: post.body_html }} />
          ) : (
            <p className="text-gray-400 text-center py-8">{post.excerpt}</p>
          )}
        </div>

        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <Tag className="w-4 h-4 text-gray-400" />
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs font-semibold text-gray-500 bg-white border border-gray-200 px-2.5 py-1 rounded-full">{tag}</span>
            ))}
          </div>
        )}
        <PromotionSlot placement="article_bottom" compact />
      </article>

      {/* Latest */}
      {latest.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-xl font-extrabold text-gray-900 mb-5">{t('news.latestTitle')}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {latest.slice(0, 3).map((i) => (
              <Link key={i.slug} to={`/news/${i.slug}`} className="group flex flex-col bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md hover:border-goszakup-200 transition-all">
                <div className="aspect-video bg-gray-100 overflow-hidden">
                  {newsImageUrl(i.cover_url) ? (
                    <img src={newsImageUrl(i.cover_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Newspaper className="w-10 h-10 text-gray-200" /></div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-extrabold text-gray-900 line-clamp-2 group-hover:text-goszakup-700 transition-colors">{listTitle(i)}</h3>
                  <div className="flex items-center gap-1 text-xs text-gray-400 mt-2">
                    <Clock className="w-3.5 h-3.5" /> {i.reading_time} {t('news.min')}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
